import { FunctionComponent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useMutation } from '@apollo/client'
import styles from './addDaycareGroupUrlDialog.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { UPDATE_DAYCARE_GROUP_URL } from '@/modules/API/daycareGroup/daycareGroupUrl'
import { DaycareGroup, DaycareGroupsUrl, DaycareGroupUrlTypes } from '@/types/DaycareGroup'
import { SelectOptionsDeprecated } from '@/types/Select'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { Daycare } from '@/types/Daycare'
import { getDaycareGroupsIconTextToJSX } from '@/modules/helper/iconsHelper'
import SelectUniqueIcon, {
    SelectUniqueIconOptions
} from '@/components/_custom/Selector/selectUniqueIcon/selectUniqueIcon'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface EditDaycareGroupUrlProps {
    open: boolean
    handleClose: () => void
    url: DaycareGroupsUrl
    group: DaycareGroup
    refetchUrl: () => void
}

const EditDaycareGroupUrlDialog: FunctionComponent<EditDaycareGroupUrlProps> = ({
    open,
    handleClose,
    url,
    group,
    refetchUrl
}: EditDaycareGroupUrlProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [urlText, setUrlText] = useState<string>(url.text)
    const [urlLink, setUrlLink] = useState<string>(url.url)
    const [urlIcon, setUrlIcon] = useState<string>(url.icon)
    const [daycareIds, setDaycareIds] = useState<Array<string>>(
        url.daycareIds ? url.daycareIds.map((daycareId) => daycareId.toString()) : []
    )

    const [editDaycareGroupURL] = useMutation(UPDATE_DAYCARE_GROUP_URL, {
        variables: {
            groupId: group.id,
            urlId: url.id,
            text: urlText,
            url: urlLink,
            icon: urlIcon,
            daycareIds: daycareIds.map((daycareId) => parseInt(daycareId))
        },
        onCompleted: () => {
            showNotificationSuccess(t('urlUpdated'))

            refetchUrl()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('urlUpdatedError'))
        }
    })

    const iconsOptions: SelectUniqueIconOptions[] = Object.values(DaycareGroupUrlTypes).map((icon) => ({
        title: icon,
        icon: getDaycareGroupsIconTextToJSX(icon, styles.SelectedIconSize)
    }))

    const daycareIdsOptions: SelectOptionsDeprecated = [
        {
            options:
                group.daycares.map((daycare: Daycare) => {
                    return {
                        name: daycare.city + ' - ' + daycare.name,
                        value: daycare.id.toString()
                    }
                }) || []
        }
    ]

    useEffect(() => {
        setUrlText(url.text)
        setUrlLink(url.url)
        setUrlIcon(url.icon)
        setDaycareIds(url.daycareIds ? url.daycareIds.map((daycareId) => daycareId.toString()) : [])
    }, [url])

    const disable: boolean =
        url.text === urlText &&
        url.url === urlLink &&
        url.icon === urlIcon &&
        JSON.stringify(url.daycareIds?.map((daycareId) => daycareId.toString())) === JSON.stringify(daycareIds)

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('editUrl')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('name')}
                        className={styles.UserInput}
                        value={urlText}
                        setValue={setUrlText}
                        required
                    />
                    <UserInput
                        label={t('url')}
                        className={styles.UserInput}
                        value={urlLink}
                        setValue={setUrlLink}
                        required
                    />
                    <SelectUniqueIcon
                        icon={getDaycareGroupsIconTextToJSX(urlIcon as DaycareGroupUrlTypes, styles.SelectedIconSize)}
                        title={t('icon')}
                        setIcon={setUrlIcon}
                        iconsOptions={iconsOptions}
                        className={styles.UserInput}
                    />
                    <SelectMultiple
                        selectOptions={daycareIdsOptions}
                        selectValues={daycareIds}
                        setSelectValues={setDaycareIds}
                        className={styles.UserInput}
                        label={t('myDaycares')}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'edit'}
                        onClick={editDaycareGroupURL}
                        disabled={disable}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditDaycareGroupUrlDialog
